import React, { useEffect, useState } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Paper,
  Avatar,
} from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  Legend,
  PieChart,
  Pie,
  Cell,
  CartesianGrid,
} from "recharts";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import PeopleIcon from "@mui/icons-material/People";
import WorkIcon from "@mui/icons-material/Work";
import EventIcon from "@mui/icons-material/Event";
import PersonIcon from "@mui/icons-material/Person";

import img from "../../Assets/Images/close-up-portrait-young-bearded-man-white-shirt-jacket-posing-camera-with-broad-smile-isolated-gray_171337-629.jpg";
import SelectInput from "../../Components/InputComponents/SelectInput";
import { axiosAPI } from "../../Components/AxiosComponents/Axios";
import { useNavigate } from "react-router-dom";

const ClientDash = () => {
  const COLORS = [
    "#30B0C7",
    "#5C7DFB",
    "#7063D4",
    "#8AE073",
    "#C274E1",
    "#E56B76",
    "#bebe00c7",
    "#F48000",
  ];

  const id =
    localStorage?.getItem("SWA_UserId") ||
    sessionStorage?.getItem("SWA_UserId");

  const currentYear = new Date().getFullYear();
  const [yearOptions, setYearOptions] = useState([]);

  const [YearlyStatYear, setYearlyStatYear] = useState(currentYear.toString());
  const [progressYear, setprogressYear] = useState(currentYear.toString());

  const [selectedJob, setSelectedJob] = useState(null);

  const Navigate = useNavigate();

  const [dashData, setDashData] = useState({});
  const [MonthlyCandidateProgression, setMonthlyCandidateProgression] =
    useState([]);
  const [clientProgress, setClientProgress] = useState([]);
  const [jobList, setJobList] = useState([]);

  const getDashData = async () => {
    try {
      const res = await axiosAPI?.post(`/clientportal/dasboard/${id}`);
      setDashData(res?.data?.data);
    } catch (err) {
      console.error(err);
    }
  };

  const getJobs = async () => {
    const trimmedFilters = {
      JD_Title: "",
      JD_Country: "",
      JD_Qualification: "",
      JD_Profession: "",
      JD_Status: "",
    };

    try {
      const res = await axiosAPI.post(
        `/clientportal/joblist/${id}`,
        trimmedFilters
      );

      const jobs = res?.data?.data || [];

      const jobListWithAll = [{ JD_Title: "All Jobs", JD_Id: null }, ...jobs];

      setJobList(jobListWithAll);
    } catch (err) {
      console.error(err);
    }
  };

  const getMonthlyCandidateStatistics = async () => {
    if (!progressYear) {
      setClientProgress([])
      return;
    }
    try {
      const res = await axiosAPI?.post(`/clientportal/Progress/${id}`, {
        year: progressYear,
        jobId: selectedJob || null,
      });
      setClientProgress(res?.data?.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const getMonthlyCandidateProgression = async () => {
    if (!YearlyStatYear) {
      setMonthlyCandidateProgression([])
      return;
    }
    try {
      const res = await axiosAPI?.post(`/clientportal/yearlyStats/${id}`, {
        year: YearlyStatYear,
      });

      const monthNames = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];

      const formattedData = res?.data?.data?.map((item) => ({
        ...item,
        month: monthNames[item.month - 1],
      }));

      setMonthlyCandidateProgression(formattedData);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    const currentYear = new Date().getFullYear();
    const startYear = 2020;
    const endYear = currentYear;

    const options = Array.from({ length: endYear - startYear + 1 }, (_, i) => {
      const year = startYear + i;
      return { label: String(year), value: String(year) };
    });

    setYearOptions(options);

    getDashData();
    getJobs();
  }, []);

  useEffect(() => {
    getMonthlyCandidateStatistics();
  }, [progressYear, selectedJob]);

  useEffect(() => {
    getMonthlyCandidateProgression();
  }, [YearlyStatYear]);

  return (
    <Paper
      sx={{
        maxWidth: `100vw`,
        padding: 2,
        margin: "auto",
        boxShadow: "none",
        overflowX: "auto",
        fontFamily: "Geist, sans-serif",
        minHeight: "100%",
      }}
    >
      <Typography
        variant="h4"
        gutterBottom
        sx={{
          textAlign: "left",
          fontFamily: "Geist, sans-serif",
          color: "#333",
          fontWeight: "600",
        }}
      >
        Dashboard
      </Typography>

      <Grid container spacing={3}>
        {[
          {
            icon: <WorkIcon sx={{ color: "#AA60BC" }} />,
            title: "Jobs",
            value: dashData?.totalJobsPosted,
            link: "/Jobs",
            color: "white",
            borderColor: "#006CB8",
            backgroundColor: "#AA60BC",
          },
          {
            icon: <PersonIcon sx={{ color: "#E267A0" }} />,
            title: "Candidates",
            value: dashData?.totalLinkedCandidates,
            link: "/Candidates",
            color: "white",
            borderColor: "#006CB8",
            backgroundColor: "#E267A0",
          },
          {
            icon: <HowToRegIcon sx={{ color: "#21B6A8" }} />,
            title: "Candidates Deployed",
            value: dashData?.totalDeployed,
            link: "/DeployedCandidates",
            color: "white",
            borderColor: "#006CB8",
            backgroundColor: "#21B6A8",
          },
        ]?.map((item, index) => (
          <Grid item xs={12} sm={6} md={6} lg={4} xl={4} key={index}>
            <Card sx={{ boxShadow: "none" }}>
              <CardContent
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  background: item?.backgroundColor,
                  borderRadius: "10px",
                  padding: 3,
                  height: "140px",
                }}
              >
                {/* Left side: Value + Title */}
                <Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontSize: "40px",
                      fontFamily: "Geist, sans-serif",
                      fontWeight: "600",
                      letterSpacing: "1px",
                      color: item?.color,
                    }}
                  >
                    {item?.value ? `${item?.value}+` : item?.value}
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: "26px",
                      fontFamily: "Geist, sans-serif",
                      fontWeight: "500",
                      color: item?.color,
                      position: "relative",
                      bottom: "10px",
                    }}
                  >
                    {item.title}
                  </Typography>
                </Box>

                {/* Right side: Avatar */}
                <Avatar
                  sx={{
                    background: `white`,
                    transition: "all 0.3s ease",
                    cursor: "pointer",
                    "&:hover": {
                      transform: "scale(1.2)",
                    },
                    width: 56,
                    height: 56,
                  }}
                  onClick={() => Navigate(item?.link)}
                >
                  {item?.icon}
                </Avatar>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3} sx={{ mt: 1 }}>
        <Grid item xs={12} md={12} lg={12} xl={6}>
          <Box
            sx={{
              p: { xs: 1, sm: 3 },
              backgroundColor: "#fff",
              borderRadius: 3,
              border: "2px solid #E5E7EB",
              height: "450px",
              paddingBottom: { xs: "70px", sm: "30px" },
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
                gap: 2,
              }}
            >
              {/* Title */}
              <Box sx={{ flexGrow: 1, minWidth: "200px" }}>
                <Typography
                  variant="h6"
                  sx={{ fontFamily: "Geist, sans-serif", color: "#333" }}
                >
                  Candidate Statistics
                </Typography>
              </Box>

              {/* Single Select Field */}
              <Box
                sx={{
                  minWidth: "200px",
                  display: "flex",
                  justifyContent: "flex-end",
                }}
              >
                <SelectInput
                  maxWidth="250px"
                  label="Year"
                  options={yearOptions}
                  value={YearlyStatYear}
                  onChange={(e) => setYearlyStatYear(e.target.value)}
                  placeholder="Select Year"
                />
              </Box>
            </Box>

            <ResponsiveContainer
              width="100%"
              height="100%"
              style={{
                padding: "40px 0px",
                position: "relative",
                right: "20px",
              }}
            >
              {MonthlyCandidateProgression?.some(
                (item) => item.totalCandidates !== 0 || item.totalJoined !== 0
              ) ? null : (
                <Box
                  height={300}
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  sx={{ color: "#999", fontSize: "18px", position: "fixed" }}
                >
                  No data to show
                </Box>
              )}

              <BarChart data={MonthlyCandidateProgression}>
                <CartesianGrid stroke="#ccc" strokeDasharray="3 3" />
                <XAxis dataKey="month" stroke="#333" />
                <YAxis stroke="#333" />
                <Legend />
                {MonthlyCandidateProgression?.some(
                  (item) => item.totalCandidates !== 0 || item.totalJoined !== 0
                ) && <Tooltip />}
                <Bar dataKey="totalCandidates" fill="#3F51B5" name="Applied" />
                <Bar dataKey="totalJoined" fill="#9B59B6" name="Deployed" />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Grid>

        <Grid item xs={12} md={12} lg={12} xl={6}>
          <Box
            sx={{
              p: { xs: 1, sm: 3 },
              backgroundColor: "#fff",
              borderRadius: 3,
              border: "2px solid #E5E7EB",
              minHeight: "450px",
              paddingBottom: { xs: "70px", sm: "30px" },
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
                gap: 2,
              }}
            >
              <Box sx={{ flexGrow: 1, minWidth: "200px" }}>
                <Typography
                  variant="h6"
                  sx={{ fontFamily: "Geist, sans-serif", color: "#333" }}
                >
                  Candidate Progression
                </Typography>
              </Box>

              {/* Filters */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 2,
                  flexGrow: 1,
                  minWidth: "250px",
                }}
              >
                <SelectInput
                  maxWidth="250px"
                  label="Year"
                  options={yearOptions}
                  onChange={(e) => {
                    setprogressYear(e.target.value);
                  }}
                  value={progressYear}
                  placeholder="Select Year"
                />

                <SelectInput
                  value={selectedJob}
                  name="jobid"
                  onChange={(e) => setSelectedJob(e?.target?.value)}
                  options={jobList?.map((item) => ({
                    value: item?.JD_Id,
                    label: item?.JD_Title,
                  }))}
                  placeholder="Select Job"
                  maxWidth="250px"
                />
              </Box>
            </Box>

            <Box
              sx={{
                display: "flex",
                flexDirection: {
                  xs: "column",
                  md: "row",
                },
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
                flexWrap: "wrap",
              }}
            >
              {/* Pie Chart Container */}
              <ResponsiveContainer width="50%" height={300}>
                {clientProgress?.every((item) => item.count === 0) ? (
                  <Box
                    height={300}
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    sx={{ color: "#999", fontSize: "18px", position: "fixed" }}
                  >
                    No data to show
                  </Box>
                ) : null}

                <PieChart>
                  <Pie
                    data={clientProgress?.filter((entry) => entry.count !== 0)}
                    dataKey="count"
                    nameKey="status"
                    cx="50%"
                    cy="50%"
                    outerRadius={110}
                    label
                  >
                    {clientProgress?.map((entry, index) => (
                      <Cell
                        key={`cell-${entry?.status}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>

              {/* Custom Legend */}
              <Box sx={{ width: "45%", pl: 2 }}>
                {clientProgress?.filter((entry) => entry.count !== 0)?.map((entry, index) => (
                  <Box
                    key={entry.status}
                    sx={{ display: "flex", alignItems: "center", mb: 1 }}
                  >
                    <Box
                      sx={{
                        width: 16,
                        height: 16,
                        backgroundColor: COLORS[index % COLORS.length],
                        borderRadius: "50%",
                        mr: 1,
                      }}
                    />
                    <Typography variant="body2">
                      {entry.status} ({entry.count})
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default ClientDash;
